import { create } from 'zustand';

export interface PriceTick {
  ticker: string;
  assetType: 'stock' | 'coin' | 'us_stock';
  name: string;
  price: number;
  changePct: number;
  updatedAt: number;
}

/** Socket.io 실시간 틱 — name/assetType 은 덮어쓰지 않음 */
export interface PriceDelta {
  ticker: string;
  price: number;
  changePct: number;
  updatedAt?: number;
}

interface MarketState {
  prices: Record<string, PriceTick>;
  kimchiRate: number | null;
  /** 실시간 틱: name/assetType 은 기존 값 보존 (없으면 fallback) */
  applyPriceDelta: (delta: PriceDelta) => void;
  /** 전체 교체 (초기 /market/prices 시딩용) */
  updatePrices: (ticks: PriceTick[]) => void;
  setKimchi: (rate: number | null) => void;
}

export const useMarketStore = create<MarketState>((set) => ({
  prices: {},
  kimchiRate: null,

  applyPriceDelta: (delta) =>
    set((state) => {
      const existing = state.prices[delta.ticker];
      // 기존 엔트리 있으면 price/changePct만 갱신 — name/assetType 보존
      if (existing) {
        return {
          prices: {
            ...state.prices,
            [delta.ticker]: {
              ...existing,
              price: delta.price,
              changePct: delta.changePct,
              updatedAt: delta.updatedAt ?? Date.now(),
            },
          },
        };
      }
      // 신규 — assetType 추정 (KRW- 로 시작하면 coin, 아니면 stock)
      const assetType: 'coin' | 'stock' = delta.ticker.startsWith('KRW-') ? 'coin' : 'stock';
      return {
        prices: {
          ...state.prices,
          [delta.ticker]: {
            ticker: delta.ticker,
            assetType,
            name: delta.ticker, // 이름 없으면 코드로 fallback — 이후 /market/prices 로 갱신됨
            price: delta.price,
            changePct: delta.changePct,
            updatedAt: delta.updatedAt ?? Date.now(),
          },
        },
      };
    }),

  updatePrices: (ticks) =>
    set((state) => {
      const next = { ...state.prices };
      for (const t of ticks) {
        const existing = next[t.ticker];
        // 기존 항목이 있고 새 항목의 name 이 ticker 코드뿐이면 기존 name 유지
        if (existing && t.name === t.ticker && existing.name && existing.name !== existing.ticker) {
          next[t.ticker] = { ...t, name: existing.name, assetType: existing.assetType };
        } else {
          next[t.ticker] = t;
        }
      }
      return { prices: next };
    }),

  setKimchi: (rate) => set({ kimchiRate: rate }),
}));
